import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {
  columnMappingToSelectedColumns,
  selectedColumnsToColumnMappings,
} from 'analysis-tool/data-frame-creation/column-mapping/ColumnMappingUtil';
import {DataFrameColumnMappingTable} from 'analysis-tool/data-frame-creation/column-mapping/DataFrameColumnMappingTable';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {DataFrameColumnDef, DataFrameColumns, DataFrameSpec} from 'data-frames/DataFrameTypes';
import {DataFrameTableMode} from 'data-frames/table/DataFrameTableConstants';
import getUserColumns from 'data-frames/util/getUserColumns';
import PersistedDataFrameTableWrapper from 'data-frames/wrapper/PersistedDataFrameTableWrapper';
import {FONT_PROXIMA_12, FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import * as Colors from 'ui-library/Colors';

export interface AnalysisTemplateInputTableColumnMapperProps {
  /**
   * The data frame of the analysis template's variable input table, whose columns are fixed and mapped
   * against.
   */
  templateTableDataFrame?: (DataFrameSpec & {id?: string}) | null;
  /**
   * The columns of the data frame of the analysis template's variable input table.
   * This will override any columns fetched from `templateTableDataFrame`.
   */
  templateTableDataFrameColumns?: Array<DataFrameColumnDef>;
  /**
   * The user-facing label for the template's variable input tables (e.g. "Expected input").
   */
  templateTableLabel: string;
  /**
   * The user-facing label for the template's variable input table columns.
   */
  templateTableColumnsHeader?: string;
  /**
   * Description to show beneath the name of the data frame from the Analysis Template.
   */
  templateTableDescription?: string;
  /**
   * A component to select the table which will be input to the Analysis.
   *
   * This component is injected because, depending on context, users may select from different data sources.
   */
  inputTableSelector: JSX.Element;
  /**
   * The user-facing label for the user-selected input tables (e.g. "Dataset to use").
   */
  inputTableSelectorLabel: string;
  /**
   * The user-facing label for the user-selected input table columns.
   */
  inputTableColumnsHeader?: string;
  /**
   * The columns of the selected input table, or null if no input table has been selected.
   *
   * Column selectors are hidden if `inputTableColumns` is null.
   */
  inputTableColumns: DataFrameColumns | null;
  /**
   * The ID of the data frame of the selected input table, if available.
   *
   * If passed, a preview of the data frame's rows will be shown.
   *
   * `inputTableDataFrameIdForPreview` may be omitted if the input tables have not yet been created, and thus
   * have no backing data frame to preview.
   */
  inputTableDataFrameIdForPreview: string | null;
  /**
   * The user-selected column mapping from `templateDataFrame.allColumnsJson` to `inputTableColumns`.
   *
   * Each key of `mappedColumns` is a column system name from `templateDataFrame.allColumnsJson`, and values
   * are column system names from `inputTableColumns`.
   *
   * The mapping may be incomplete, i.e. columns from `templateDataFrame.allColumnsJson` that have not been
   * mapped will be absent. To check the mapping is complete, see `isDataFrameColumnMappingComplete`.
   */
  columnMapping: Record<string, string>;
  /**
   * Callback to update the column mapping.
   *
   * The mapping may be incomplete, i.e. columns from `templateDataFrame.allColumnsJson` that have not been
   * mapped will be absent. To check the mapping is complete, see `isDataFrameColumnMappingComplete`.
   */
  setColumnMapping: (newMapping: Record<string, string>) => void;
  /**
   * Instructions to display at the top of the configuration.
   */
  instructions?: React.ReactNode;
  /**
   * Enforces that any column in the input table can only be mapped to one column in the template table.
   */
  isInputTableColumnUnique?: boolean;
}

/**
 * A component to map the columns of a table (to be input to an analysis) to the columns of an analysis
 * template's input table.
 *
 * Previews are shown of the template data frame and, if it exists, the selected input table.
 *
 * Soft warnings are shown for column type mismatches.
 */
export default function AnalysisTemplateInputTableColumnMapper(
  props: AnalysisTemplateInputTableColumnMapperProps
): JSX.Element {
  const templateDataFrameColumns = getUserColumns({
    columns: props.templateTableDataFrameColumns ?? props.templateTableDataFrame?.columns ?? [],
  });

  return (
    <div className={css(styles.wrapper)} role="region" aria-label="Column mapper">
      <div className={css(styles.leftContentPane)}>
        {props.instructions == null ? null : (
          <div className={css(styles.instructions)}>{props.instructions}</div>
        )}
        <Row className={css(styles.headerRow)}>
          <Col md={6}>{props.templateTableLabel}</Col>
          <Col md={6}>{props.inputTableSelectorLabel}</Col>
        </Row>
        <Row>
          <Col md={6}>{props.templateTableDataFrame?.name}</Col>
          <Col md={6}>{props.inputTableSelector}</Col>
        </Row>
        {props.templateTableDescription && (
          <Row>
            <Col md={6}>
              <span className={css(styles.tableDescription)}>{props.templateTableDescription}</span>
            </Col>
          </Row>
        )}
        <Row className={css(styles.headerRow, styles.tableSpacing)}>
          <Col md={6}>{props.templateTableColumnsHeader ?? 'Table column'}</Col>
          <Col md={6}>{props.inputTableColumnsHeader ?? 'Table column'}</Col>
        </Row>
        {templateDataFrameColumns.length > 0 && (
          <DataFrameColumnMappingTable
            newDataFrameColumns={props.inputTableColumns}
            templateDataFrameColumns={templateDataFrameColumns}
            setSelectedColumns={(newSelectedColumns) => {
              props.setColumnMapping(
                selectedColumnsToColumnMappings(newSelectedColumns, templateDataFrameColumns)
              );
            }}
            selectedColumns={columnMappingToSelectedColumns(
              props.columnMapping,
              templateDataFrameColumns,
              props.inputTableColumns ?? []
            )}
            isInputTableColumnUnique={props.isInputTableColumnUnique}
          />
        )}
      </div>
      <div className={css(styles.previewAreaPane)}>
        {props.templateTableDataFrame?.id && (
          <div className={css(styles.previewTableWrapper)}>
            <PersistedDataFrameTableWrapper
              dataFrameId={props.templateTableDataFrame.id}
              actionOrigin={ActionOrigin.ANALYSIS_TOOL}
              clientStateId={`analysis-template-input-table-preview-${props.inputTableDataFrameIdForPreview}`}
              defaultTableMode={DataFrameTableMode.RICH}
              renderIsExcludedColumn={false}
            />
          </div>
        )}
        {props.inputTableDataFrameIdForPreview && (
          // TODO(BNCH-103905): Show a column-only preview if `inputTableDataFrameId` is not available
          <div className={css(styles.previewTableWrapper, styles.tableSpacing)}>
            <PersistedDataFrameTableWrapper
              dataFrameId={props.inputTableDataFrameIdForPreview}
              actionOrigin={ActionOrigin.ANALYSIS_TOOL}
              clientStateId={`analysis-template-selected-table-preview-${props.inputTableDataFrameIdForPreview}`}
              defaultTableMode={DataFrameTableMode.RICH}
              renderIsExcludedColumn={false}
            />
          </div>
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  headerRow: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  instructions: {
    marginBottom: 10,
  },
  leftContentPane: {
    flex: 1,
    ...FONT_PROXIMA_13,
    padding: 16,
  },
  previewAreaPane: {
    padding: 12,
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    overflowX: 'hidden',
  },
  previewTableWrapper: {
    background: Colors.GRAY_LIGHTER,
    maxHeight: 350,
  },
  tableDescription: {
    ...FONT_PROXIMA_12,
    color: Colors.GRAY_SEMIDARK,
  },
  tableSpacing: {
    marginTop: 16,
  },
  wrapper: {
    backgroundColor: Colors.WHITE,
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    width: '100%',
  },
});
